import { ref } from 'vue';

/** The logo shown when an administrator has not uploaded one. */
export const DEFAULT_LOGO_URL = '/storage/logo/logo_light.svg';

/**
 * Reads the application settings the layout injects on to `window`.
 *
 * The settings are snapshotted when this runs, so call it where the value is
 * needed rather than holding on to the result across a navigation.
 */
export function useSettings() {
  const settings = ref<Window['settings']>(window.settings ?? {});

  /**
   * Returns the setting behind `sid`, typed as `AppSettings` declares it.
   *
   * Falsy values map to null, as they always have: callers treat an unset and
   * an empty setting the same way. The cast is the one place that knows this —
   * while `K` is generic the compiler cannot see that `|| null` is what removes
   * the keys the unauthenticated layout leaves out.
   */
  const getSetting = <K extends keyof AppSettings>(sid: K): AppSettings[K] | null => (settings.value[sid] as AppSettings[K]) || null;

  /** The uploaded logo, or the default logo when none was uploaded. */
  const logoUrl = getSetting('logo_url') ?? DEFAULT_LOGO_URL;

  return {
    getSetting,
    logoUrl,
  };
}
